import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { Card, Button, Row, Col, message, Tag, FloatButton } from "antd";
import { getUtmTagId, serverFetch, cnvToMultiline, emptyImage } from "@extensions";
import { Auth } from "@components";
import Payment from "../payment/payment";
import "./catalog.css";
import CatalogHeader from "./catalog-header.js";
import CatalogSale from "./catalog-sale";
import { useGetIsDesktop, useGetIsPhone } from "@extensions";

const emptyGroups = [{ id: 0, name: "Загрузка ...", description: "" }];
const emptyItems = [
  { id: 0, name: "", description: "", catalogGroupId: 0 },
  { id: 1, name: "", description: "", catalogGroupId: 0 },
  { id: 2, name: "", description: "", catalogGroupId: 0 },
  { id: 3, name: "", description: "", catalogGroupId: 0 },
];

const Catalog = () => {
  const user = useSelector((s) => s.user);
  const company = useSelector((s) => s.company);
  const { id } = useParams();
  const [groups, setGroups] = useState(emptyGroups);
  const [items, setItems] = useState(emptyItems);
  const [currentItem, setCurrentItem] = useState();
  const [currentRents, setCurrentRents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openItem, setOpenItem] = useState(false);
  const [showSignin, setShowSignin] = useState(false);
  const [scroll, setScroll] = useState(false);
  const [autopay, setAutopay] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [currentGroupId, setCurrentGroupId] = useState();
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentSales, setPaymentSales] = useState([]);
  const isDesktop = useGetIsDesktop();
  const isPhone = useGetIsPhone();
  
  useEffect(() => {
    const contentContainer = document.querySelector(".content-container");

    if (company && contentContainer) {
      if (company.catalogBackgroundImage) {
        contentContainer.style.backgroundImage = `url("${company.catalogBackgroundImage}")`;
      } else {
        contentContainer.style.backgroundImage = "";
      }
    }

    return () => {
      if (contentContainer) {
        contentContainer.style.backgroundImage = "";
      }
    };
  }, [company]);

  useEffect(() => {
    if (!company) return;

    let cleanup = false;

    const fetchData = async () => {
      setLoading(true);

      try {
        const queryParams = {
          companyId: company.id,
          isActive: true,
          noImage: true,
        };

        const [groupData, itemData] = await Promise.all([
          serverFetch("Online/CatalogGroups", { method: "GET", queryParams }, user).catch(() => {
            message.error("Ошибка получения групп каталога");
            return [];
          }),
          serverFetch("Online/Products", { method: "GET" }).catch(() => {
            message.error("Ошибка получения элементов каталога");
            return [];
          }),
        ]);

        if (cleanup) return;

        setGroups(groupData || []);
        setItems(itemData || []);
      } finally {
        if (!cleanup) {
          setLoading(false);
          setLoaded(true);
        }
      }
    };

    fetchData();
    return () => {
      cleanup = true;
    };
  }, [company, user]);

  useEffect(() => {
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (user && currentItem) setOpenItem(true);
  }, [currentItem, user]);

  useEffect(() => {
    if (loaded && id && groups && !scroll) {
      const element = document.getElementById(`${id}`);
      if (element) {
        setScroll(true);
        window.history.replaceState(null, null, "/catalog");
        setTimeout(() => element.scrollIntoView({ behavior: "smooth", block: "start" }), 1500);
      }
    }
  });

  useEffect(() => {
    document.addEventListener("scroll", handleScroll);
    return () => document.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const root = document.documentElement;

    const updateCatalogOffsets = () => {
      const header = document.getElementById("group-header");
      if (!header) return;

      const fixedTop = isDesktop ? 88 : 76;
      const offset = Math.ceil(header.getBoundingClientRect().height + fixedTop + 16);

      root.style.setProperty("--catalog-section-offset", `${offset}px`);
      root.style.setProperty("--catalog-trigger-point", `${offset}px`);
    };

    updateCatalogOffsets();

    const header = document.getElementById("group-header");
    const resizeObserver = header && "ResizeObserver" in window ? new ResizeObserver(updateCatalogOffsets) : null;

    if (header && resizeObserver) {
      resizeObserver.observe(header);
    }

    window.addEventListener("resize", updateCatalogOffsets);
    window.addEventListener("orientationchange", updateCatalogOffsets);

    return () => {
      window.removeEventListener("resize", updateCatalogOffsets);
      window.removeEventListener("orientationchange", updateCatalogOffsets);
      resizeObserver?.disconnect();
    };
  }, [isDesktop, groups.length]);

  const handleScroll = () => {
  const scrollPosition = window.scrollY;
  const windowHeight = window.innerHeight;
  const docHeight = document.documentElement.scrollHeight;
  
  // Всегда выбираем элементы по классу .group
  const elements = document.querySelectorAll(".group");
  let currentElementId = null;

  const header = document.getElementById("group-header");
  const triggerPointValue = window.getComputedStyle(document.documentElement).getPropertyValue("--catalog-trigger-point").trim();
  const triggerPoint = Number.parseFloat(triggerPointValue) || (header ? Math.ceil(header.getBoundingClientRect().height + (isDesktop ? 88 : 76) + 16) : 160);

  // Проверка на самый низ страницы (учитываем футер каталога)
  const isBottom = Math.ceil(scrollPosition + windowHeight) >= docHeight - 15;

  if (isBottom && elements.length > 0) {
    // В самом низу принудительно берем последнюю группу
    const lastElement = elements[elements.length - 1];
    currentElementId = lastElement.id.replace("catalog-group-", "");
  } else {
    // Ищем, какой элемент сейчас пересекает линию triggerPoint
    elements.forEach((element) => {
      const rect = element.getBoundingClientRect();
      
      // Если верхняя граница блока уже поднялась выше 160px, 
      // а нижняя граница блока еще не ушла выше 160px — этот блок активен
      if (rect.top <= triggerPoint && rect.bottom > triggerPoint) {
        currentElementId = element.id.replace("catalog-group-", "");
      }
    });
  }

  // Если нашли активную группу, обновляем стейт и скроллим меню табов
  if (currentElementId) {
    const groupIdNumber = Number(currentElementId);
    setCurrentGroupId(groupIdNumber);

    const el = document.getElementById("group-header");
    // Ищем таб в шапке (убедитесь, что у табов id формата group-ID, например group-12)
    const elc = document.getElementById(`group-${currentElementId}`); 
    
    if (el && elc) {
      const delta = elc.getBoundingClientRect().left - el.getBoundingClientRect().left;
      el.scrollBy({ left: delta, behavior: "smooth" });
    }
  }
};

  const onPayNew = (paymentSystem, saleId, autopayValue) => {
    serverFetch("payment/sale", { method: "POST", bodyData: { saleId, paymentSystem, autopay: autopayValue } })
      .then((data) => (window.location.href = data))
      .catch((data) => message.error(data.UserMessage));
  };

  const onPay = (system) => {
    const sale = {
      companyId: company.id,
      ...currentItem,
      externalId: null,
      utmId: getUtmTagId(),
    };

    if (currentRents.length === 0) {
      serverFetch("sale", { method: "POST", bodyData: sale }, user)
        .then((data) => {
          setCurrentItem();
          onPayNew(system, data.id, autopay);
        })
        .catch((e) => {
          setLoading(false);
          e.UserMessage && message.error(e.UserMessage);
        });
      return;
    }

    const sales = [sale];
    currentRents.forEach((item) =>
      sales.push({
        ...sale,
        catalogId: 0,
        productId: item.productId,
        rentSize: item.size,
        price: 0,
        totalPrice: 0,
        qtyClients: 1,
        externalId: null,
      }),
    );

    serverFetch("sale/basket", { method: "POST", bodyData: sales }, user)
      .then((data) => {
        setCurrentItem();
        onPayNew(system, data.id, autopay);
      })
      .catch((e) => {
        setLoading(false);
        e.UserMessage && message.error(e.UserMessage);
      });
  };

  const onOpenItem = (item) => {
    setCurrentRents([]);
    setAutopay(item.autopay || false);
    setCurrentItem({ ...item, catalogId: item.id });
    setOpenItem(true);
    window.parent.postMessage({ sender: "proffit_scroll" }, "*");
  };

  const onCancel = () => {
    setCurrentItem();
    setOpenItem(false);
  };

  const onClickHeader = (groupId) => {
    const element = document.getElementById(groupId);
    if (element) {
      setScroll(true);
      setCurrentGroupId(groupId);
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const renderBadge = (item) => {
    if (item.bestPrice) {
      return <span className="catalog-item-badge catalog-item-badge-best">Лучшая цена</span>;
    }
    if (item.bestSeller) {
      return <span className="catalog-item-badge catalog-item-badge-hot">Хит продаж</span>;
    }
    if (item.action) {
      return <span className="catalog-item-badge catalog-item-badge-action">Акция</span>;
    }
    return null;
  };

  const getActionLabel = (item) => (item?.autopay ? "Записаться" : "Купить");

  const divMobileHeaders = groups && (
    <CatalogHeader
      groups={groups}
      currentGroupId={currentGroupId}
      onClick={onClickHeader}
    />
  );

  const divDesktopHeaders = groups && (
    <CatalogHeader
      groups={groups}
      currentGroupId={currentGroupId}
      onClick={onClickHeader}
      desktop={true}
    />
  );

  const renderItems = () => {
    return (
      <div className="catalog-page">
        {isDesktop ? (
          <div className="catalog-desktop-nav-wrap">{divDesktopHeaders}</div>
        ) : (
          divMobileHeaders
        )}

        <div className="block-content">
          {groups &&
            groups.map((g) => {
              const filteredItems = items.filter((i) => i.catalogGroupId === g.id);
              if (filteredItems.length <= 0) return false;

              return (
                <div key={g.id} id={`${g.id}`} className="group catalog-section">
                  <div className="catalog-group-title catalog-section-header">
                    <div className="catalog-section-copy">
                      <div className="catalog-section-kicker">Категория</div>
                      <div className="catalog-section-title">{g.name}</div>
                      {g.description ? <div className="catalog-section-description">{g.description}</div> : null}
                    </div>
                    <div className="catalog-section-image-wrap" aria-hidden="true">
                      <img
                        className="catalog-section-image"
                        alt=""
                        src={g.imageId ? `${process.env.REACT_APP_API_BASE_URL}/api/image/${g.imageId}.jpg` : emptyImage}
                      />
                    </div>
                  </div>

                  <Row gutter={[24, 24]} className="catalog-card-row">
                    {filteredItems.map((i) => {
                      const tagCredit =
                        company?.payCredit && !i.autopay && i.price >= 3000 ? (
                          <Tag color="#FEDD2C" className="catalog-credit-tag" style={{ color: "black" }}>
                            В кредит от {Math.round(i.price / 20)} ₽ в месяц
                          </Tag>
                        ) : null;

                      const divDiscountPrice =
                        i.productPrice && i.qty === 1 && i.productPrice > i.price ? (
                          <div className="catalog-price-discount">{i.productPrice.toLocaleString()} ₽</div>
                        ) : null;

                      const divPrice =
                        i.price && i.price !== 0 ? (
                          <div className="catalog-item-price">{i.price.toLocaleString()}</div>
                        ) : null;

                      return (
                        <Col key={i.id} span={isPhone ? 24 : isDesktop ? 8 : 12}>
                          <Card className="catalog-item-card flexible-card" loading={loading}>
                            <div className="catalog-item-top">
                              {renderBadge(i)}
                              <div className="catalog-item-title">{i.name}</div>
                              <div className="catalog-item-description">{cnvToMultiline(i.description)}</div>
                              <div className="catalog-item-tags">{tagCredit}</div>
                            </div>

                            <div className="catalog-item-bottom">
                              <div className="catalog-item-price-wrap">
                                {divDiscountPrice}
                                {divPrice}
                              </div>
                              <Button
                                className="catalog-item-btn catalog-item-cta"
                                onClick={() => onOpenItem(i)}
                                size="middle"
                              >
                                {getActionLabel(i)}
                              </Button>
                            </div>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                </div>
              );
            })}
        </div>
      </div>
    );
  };

  const onItemChange = (data) => {
    if (!currentItem) return;
    const newx = Object.assign({ ...currentItem }, data);
    newx.qty = currentItem.qty;
    setCurrentItem(newx);
  };

  return (
    <>
      {renderItems()}
      {currentItem && (
        <CatalogSale
          item={currentItem}
          open={openItem}
          onClose={onCancel}
          onPay={onPay}
          onItemChange={onItemChange}
          onRentsChange={setCurrentRents}
          autopay={autopay}
          onAutopayChange={setAutopay}
          currentRents={currentRents}
          onPaymentOpen={(sales) => {
            setPaymentSales(sales);
            setPaymentOpen(true);
          }}
        />
      )}
      {showSignin && <Auth open={true} onClose={() => setShowSignin(false)} />}
      <Payment sales={paymentSales} open={paymentOpen} onClose={() => setPaymentOpen(false)} />
      <FloatButton.BackTop />
    </>
  );
};

export default Catalog;
